import { ApiError } from '../api/client';
import type { SkippedOperatingUnit } from './api';

/** Same 403/400/fallback idiom `delivery-note-api-errors.ts`'s `genericApiErrorMessage` already applies. */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

const UNIT_NAME_IN_MESSAGE = /operating unit "([^"]+)"/i;

/** The unit a draft-generation 422 names in its (English) `message`, or `null` when it names none. */
function operatingUnitNameOf(error: ApiError): string | null {
  const message = (error.body as { message?: unknown } | null)?.message;

  return typeof message === 'string' ? (UNIT_NAME_IN_MESSAGE.exec(message)?.[1] ?? null) : null;
}

/** Why a draft run left a unit unbilled, fixed by editing its fiscal point of sale in Configuración. */
export function skippedOperatingUnitMessage(unit: SkippedOperatingUnit): string {
  return unit.reason === 'OPERATING_UNIT_WITHOUT_FISCAL_POINT_OF_SALE'
    ? `El punto de venta "${unit.operatingUnitName}" no tiene un PV fiscal vinculado.`
    : `El punto de venta "${unit.operatingUnitName}" tiene el PV fiscal desactivado o sin ese tipo de comprobante.`;
}

/** Codes from `BillingExceptionFilter` (`apps/api` `billing-exception.filter.ts`). */
export function mapInvoiceError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'EXCLUDE_REASON_REQUIRED') {
    return 'El motivo de exclusión es obligatorio.';
  }

  if (error.status === 409 && code === 'INVOICE_NOT_EDITABLE') {
    return 'Esa factura ya no admite esta acción en su estado actual.';
  }

  if (error.status === 422 && code === 'INVOICE_EMPTY') {
    return 'La factura no tiene líneas y no se puede emitir.';
  }

  if (error.status === 422 && code === 'INVOICE_INVALID_TAX_ID') {
    return 'El CUIT del cliente no es válido. Corregilo antes de emitir.';
  }

  if (error.status === 422 && code === 'INVOICE_INCONSISTENT_TOTALS') {
    return 'Los totales de la factura no coinciden con sus líneas. Volvé a generar el borrador.';
  }

  if (error.status === 422 && code === 'POINT_OF_SALE_INACTIVE') {
    return 'El punto de venta de esta factura está desactivado.';
  }

  if (error.status === 409 && code === 'INVOICE_ISSUANCE_IN_PROGRESS') {
    return 'Hay otra emisión en curso para ese punto de venta. Intentá de nuevo en unos segundos.';
  }

  if (error.status === 409 && code === 'INVOICE_REJECTED_REQUIRES_CORRECTION') {
    return 'Esta factura fue rechazada: volvela a borrador antes de emitirla de nuevo.';
  }

  if (error.status === 409 && code === 'INVOICE_NOT_ISSUED') {
    return 'Esta factura todavía no fue emitida, no tiene PDF disponible.';
  }

  if (error.status === 422 && code === 'OPERATING_UNIT_WITHOUT_FISCAL_POINT_OF_SALE') {
    const unitName = operatingUnitNameOf(error);

    return unitName === null
      ? 'Un punto de venta no tiene un PV fiscal vinculado. Vinculalo antes de generar los borradores.'
      : `El punto de venta "${unitName}" no tiene un PV fiscal vinculado. Vinculalo antes de generar los borradores.`;
  }

  if (error.status === 422 && code === 'NO_FISCAL_POINT_OF_SALE_AVAILABLE') {
    const unitName = operatingUnitNameOf(error);

    if (unitName !== null) {
      return `El PV fiscal del punto de venta "${unitName}" está desactivado o no habilita este tipo de comprobante.`;
    }

    return 'No hay ningún punto de venta activo habilitado para este tipo de comprobante.';
  }

  if (error.status === 409 && code === 'POINT_OF_SALE_NUMBER_DUPLICATE') {
    return 'Ya existe un punto de venta con ese número.';
  }

  if (error.status === 409 && code === 'POINT_OF_SALE_TYPE_IN_USE') {
    return 'Ese tipo de comprobante ya tiene numeración autorizada y no se puede quitar.';
  }

  if (error.status === 502 && code === 'ARCA_AUTHENTICATION_FAILED') {
    return 'No pudimos autenticarnos con ARCA. Revisá el certificado y la fecha y hora del servidor en Configuración fiscal y usá "Reconciliar" sobre la factura.';
  }

  if (error.status === 504 && code === 'ARCA_TIMEOUT_PENDING_RECONCILIATION') {
    return 'ARCA no respondió a tiempo. La factura quedó pendiente: usá "Reconciliar" en unos minutos.';
  }

  if (error.status === 422 && code === 'FISCAL_CERTIFICATE_EXPIRED') {
    return 'El certificado fiscal está vencido. Cargá uno nuevo en Configuración fiscal antes de emitir.';
  }

  if (error.status === 422 && code === 'FISCAL_CERTIFICATE_MISSING') {
    return 'Falta cargar el certificado fiscal. Cargalo en Configuración fiscal antes de emitir.';
  }

  return genericApiErrorMessage(error);
}
